import { useCallback, useMemo } from 'react';
import { useMsal as useMsalReact, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { loginRequest, tokenRequest, extractUserRoles } from '../config/msal';

/**
 * Custom hook for MSAL authentication
 * Wraps @azure/msal-react with additional functionality
 */
export const useMsal = () => {
  const { instance, accounts, inProgress } = useMsalReact();
  const isAuthenticated = useIsAuthenticated();

  const account = accounts[0] || null;

  /**
   * Login with popup
   */
  const loginPopup = useCallback(async () => {
    try {
      const response = await instance.loginPopup(loginRequest);
      return response;
    } catch (error) {
      console.error('Login popup error:', error);
      throw error;
    }
  }, [instance]);

  /**
   * Login with redirect
   */
  const loginRedirect = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login redirect error:', error);
      throw error;
    }
  }, [instance]);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      await instance.logoutPopup({
        account: account,
      });
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback to redirect logout
      await instance.logoutRedirect({
        account: account,
      });
    }
  }, [instance, account]);

  /**
   * Get access token silently
   * Falls back to interactive login if silent acquisition fails
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Silent token acquisition failed, try interactive
        try {
          const response = await instance.acquireTokenPopup({
            ...tokenRequest,
            account: account,
          });
          return response.accessToken;
        } catch (popupError) {
          console.error('Interactive token acquisition failed:', popupError);
          return null;
        }
      }
      console.error('Token acquisition error:', error);
      return null;
    }
  }, [instance, account]);

  /**
   * Get user information from account
   */
  const getUserInfo = useCallback(() => {
    if (!account) {
      return null;
    }

    const claims = account.idTokenClaims as any;
    const roles = extractUserRoles(account);

    return {
      id: account.localAccountId,
      email: account.username,
      name: claims?.name || account.name || account.username,
      role: roles[0] || null, // Primary role
      roles: roles, // All roles
      oid: claims?.oid, // Object ID
    };
  }, [account]);

  // Memoize userInfo to prevent infinite re-render loops
  // This ensures the same object reference is returned unless account changes
  const userInfo = useMemo(() => getUserInfo(), [getUserInfo]);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (role: string): boolean => {
      if (!account) {
        return false;
      }
      const roles = extractUserRoles(account);
      return roles.includes(role);
    },
    [account]
  );

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      if (!account) {
        return false;
      }
      const userRoles = extractUserRoles(account);
      return roles.some((role) => userRoles.includes(role));
    },
    [account]
  );

  /**
   * Check if user has all of the specified roles
   */
  const hasAllRoles = useCallback(
    (roles: string[]): boolean => {
      if (!account) {
        return false;
      }
      const userRoles = extractUserRoles(account);
      return roles.every((role) => userRoles.includes(role));
    },
    [account]
  );

  return {
    // MSAL instance and state
    instance,
    accounts,
    account,
    inProgress,
    isAuthenticated,
    isLoading: inProgress !== InteractionStatus.None,

    // Authentication methods
    loginPopup,
    loginRedirect,
    logout,
    getAccessToken,

    // User information
    getUserInfo,
    userInfo,

    // Role checking
    hasRole,
    hasAnyRole,
    hasAllRoles,
  };
};
